import React from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {ContentHotel} from '@findhotel/sapi/dist/types/packages/core/src'
import {BottomCTA} from 'components/Accommodation/Offers/BottomCTA'
import {RoundShareButton} from 'components/Accommodation/ShareButton'
import {
  ShareComponent,
  ShareType
} from 'components/Accommodation/ShareButton/constants'
import SpinnerImportLoader from 'components/Loaders/SpinnerImportLoader'
import {
  getHotelMoreLocationInfoOverlay,
  getLocationVisibility
} from 'modules/overlay/selectors'
import {hideOverlay} from 'modules/overlay/slice'
import {getSearchBoxValues} from 'modules/searchBox/selectors'
import {loadModule} from 'utils'

import {BottomSheet} from '@vio/atlas/BottomSheet'
import {Button} from '@vio/atlas/Button'
import {Box} from '@vio/atlas/helpers/Box'
import {Icon} from '@vio/atlas/Icon'
import {ANIMATION_TYPES, Overlay} from '@vio/atlas/Overlay'
import FormattedMessageWrapper from '@vio/core/src/localization/components/FormattedMessage'
import {AccommodationFloatingHeader} from '@vio/shared/src/accommodation/AccommodationFloatingHeader'

import {getIsFilteredRoomsResultEmpty} from '../../../../modules/searchApi/selectors'
import {HotelLocationContent} from '../../HotelLocationContent'
import {
  ExtendedMapContainer,
  MapOverlapBottomCTAPositioner,
  OverlayContent
} from '../styles'

import type {LocationMapRendererProps} from 'components/Accommodation/LocationOverlay/LocationMapRenderer/LocationMapRenderer'

type PropsType = {
  hotel: ContentHotel | undefined
}

const LocationMapRenderer = loadModule<LocationMapRendererProps>(
  async () =>
    import(
      /* webpackChunkName: "LocationMapRenderer" */
      'components/Accommodation/LocationOverlay/LocationMapRenderer/LocationMapRenderer'
    ),
  {loading: SpinnerImportLoader}
)

export const LocationOverlayMobile = ({hotel}: PropsType) => {
  const locationOverlayIsActive = useSelector(getLocationVisibility)
  const locationInfoOverlayIsActive = useSelector(
    getHotelMoreLocationInfoOverlay
  )
  const hasOffer = !useSelector(getIsFilteredRoomsResultEmpty)

  const dispatch = useDispatch()
  const handleHideOverlay = () => dispatch(hideOverlay())
  const {checkIn, checkOut, rooms} = useSelector(getSearchBoxValues)
  const shareUrlParams = {
    hotelId: hotel?.objectID,
    checkIn,
    checkOut,
    rooms,
    component: ShareComponent.LocationOverlay
  }

  return (
    <>
      <Overlay
        isOpen={locationOverlayIsActive}
        shadeBackground={false}
        animationType={ANIMATION_TYPES.NONE}
      >
        {locationOverlayIsActive && hotel && (
          <OverlayContent>
            <ExtendedMapContainer>
              <LocationMapRenderer
                isMobile
                lat={hotel._geoloc.lat}
                lon={hotel._geoloc.lon}
              />
            </ExtendedMapContainer>
            <MapOverlapBottomCTAPositioner>
              <BottomCTA
                hotelId={hotel.objectID}
                sourceComponent="MobileHotelViewMapStaticOfferCTA"
              />
            </MapOverlapBottomCTAPositioner>
          </OverlayContent>
        )}
        <AccommodationFloatingHeader
          rightSlot={
            <RoundShareButton
              shareParams={shareUrlParams}
              component={ShareComponent.LocationOverlay}
              shareType={ShareType.ShareLink}
            />
          }
          leftSlot={
            <Button
              variant="tertiary"
              isRounded
              isFloating
              iconStart={<Icon name="Close" />}
              dataId="LocationOverlayClose"
              onClick={handleHideOverlay}
              size="lg"
            />
          }
        />
      </Overlay>
      {/* Location info bottom sheet for mobile */}
      <BottomSheet
        prerenderContent
        navBarProps={{
          centerContent: (
            <FormattedMessageWrapper
              id="location.info"
              defaultMessage="Location info"
            />
          )
        }}
        withDragHandler
        footerContent={
          hasOffer && (
            <BottomCTA
              hotelId={hotel?.objectID}
              sourceComponent="MobileHotelLocationStaticOfferCTA"
            />
          )
        }
        isOpen={locationInfoOverlayIsActive}
        onClose={handleHideOverlay}
        prefersReducedMotion
        hasFixedHeight={true}
        snapPoints={{
          top: 'nearFull',
          middle: 0.97
        }}
      >
        <Box
          paddingBottom="s800"
          paddingTop="s600"
          data-id="HotelLocationContentBottomSheet"
        >
          <HotelLocationContent
            variant="bottomSheet"
            descriptions={hotel?.descriptions}
            attractions={hotel?.location?.poi}
          />
        </Box>
      </BottomSheet>
    </>
  )
}
